import { useCallback, useMemo, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { SearchModality, SearchParams, SearchType } from '@/components/search/types';
import { useGetAlleMaterieeltypenApi } from '@/queries/stamgegevens/useGetAlleMaterieeltypenApi';
import useClientState from '@/state/useClientState';
import { getDaySelectionVariations } from '@/utils/dateVariations';

type UseSearchPanelProps = {
  searchType: SearchType;
  showPanel: false | 'searchPanel' | 'searchDates' | 'materieeltypen';
  isWithinAdvancedSearch: boolean;
};
const useSearchPanels = ({ searchType, showPanel, isWithinAdvancedSearch }: UseSearchPanelProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  // Data for when searchType = 'train' | 'trainAndRollingStock' | 'omloop'
  const daySelectionVariations = getDaySelectionVariations();

  // Data for when searchType = 'rollingStockType'
  const { data: materieeltypen } = useGetAlleMaterieeltypenApi(searchType === 'rollingStockType');

  const [_searchHistory, setSearchHistory] = useLocalStorage<SearchParams[]>('searchHistory', []);

  const addSearchResults = useClientState((state) => state.addSearchResults);
  const setAddSearchResults = useClientState((state) => state.toggleAddSearchResults);

  /** Since the SearchParams do get stored in localStorage, there should be support for searchParams v1 and v2 simultaniously. */
  const searchParamsBackwardsCompatibility = (historic: SearchParams) => {
    if ((historic?.searchModality as SearchModality | 'stringArray') === 'stringArray')
      historic.searchModality = 'multiple';
    return historic;
  };

  const searchHistory = useMemo(() => {
    return (
      isWithinAdvancedSearch
        ? _searchHistory.filter((historic) => !(isWithinAdvancedSearch && historic?.searchType === 'rollingStockType'))
        : _searchHistory
    ).map(searchParamsBackwardsCompatibility);
  }, [_searchHistory, isWithinAdvancedSearch]);

  const currentListSize = useMemo<number>(() => {
    if (showPanel === 'searchPanel') return searchHistory.length;
    if (showPanel === 'searchDates') return daySelectionVariations.length;
    if (showPanel === 'materieeltypen') return materieeltypen?.length ?? 0;
    return 0;
  }, [showPanel, daySelectionVariations, searchHistory, materieeltypen]);

  const arrowDown = useCallback(() => {
    setSelectedIndex((prev) => {
      if (currentListSize === 0 && showPanel === false) return 0;
      return prev !== 0 && prev === currentListSize - 1 ? prev : prev + 1;
    });
  }, [currentListSize, showPanel]);

  const arrowUp = useCallback(() => setSelectedIndex((prev) => (prev === 0 ? prev : prev - 1)), [showPanel]);

  const isSelected = useCallback((index: number) => index === selectedIndex, [selectedIndex]);

  const updateSearchHistory = (newSearchTerm: SearchParams) =>
    setSearchHistory((history) =>
      [newSearchTerm, ...history.filter((e) => JSON.stringify(e) !== JSON.stringify(newSearchTerm))]
        .map(searchParamsBackwardsCompatibility)
        .slice(0, 5),
    );

  return {
    arrowDown,
    arrowUp,
    isSelected,
    selectedIndex,
    daySelectionVariations,
    materieeltypen,
    searchHistory,
    updateSearchHistory,
    setSelectedIndex,
    addSearchResults,
    setAddSearchResults,
  };
};
export default useSearchPanels;
